// ---------------------------------------------------------------------------
// UBO Chain Display — Visual ownership chain for KYB review
// ---------------------------------------------------------------------------

import React from 'react';
import { StatusBadge } from './StatusBadge';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UboChainNode {
  nodeId: string;
  name: string;
  nodeType: 'entity' | 'person';
  ownershipPercentage: number;
}

export interface UboChainData {
  rootEntityId: string;
  links: UboChainNode[];
  effectiveOwnership: number;
  uboNodeId: string;
}

export interface UboDisplayItem {
  id: string;
  name: string;
  ownershipPercentage: number;
  nationality: string | null;
  verificationStatus: string;
  linkedCaseId: string | null;
}

interface UboChainDisplayProps {
  /** List of UBOs with their metadata. */
  ubos: UboDisplayItem[];
  /** Ownership chains to visualize (optional — shows simple list if absent). */
  chains?: UboChainData[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function PercentageBar({ percentage }: { percentage: number }) {
  const clampedPct = Math.min(Math.max(percentage, 0), 100);
  const barColor =
    clampedPct >= 50
      ? 'bg-primary'
      : clampedPct >= 25
        ? 'bg-primary'
        : 'bg-primary-light';

  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-24 rounded-sm bg-surface-sunken overflow-hidden">
        <div
          className={`h-full rounded-sm ${barColor}`}
          style={{ width: `${clampedPct}%` }}
        />
      </div>
      <span className="text-xs font-medium text-ink">
        {clampedPct.toFixed(1)}%
      </span>
    </div>
  );
}

function ChainArrow() {
  return (
    <div className="flex items-center justify-center py-1" aria-hidden="true">
      <svg
        className="h-4 w-4 text-ink-muted"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function UboChainDisplay({ ubos, chains }: UboChainDisplayProps) {
  if (ubos.length === 0) {
    return (
      <div className="rounded-sm border border-hairline bg-surface-sunken p-6 text-center text-sm text-ink-muted">
        No UBOs identified for this entity.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* UBO Summary List */}
      <div className="bg-surface-raised rounded-sm shadow overflow-hidden">
        <div className="px-4 py-3 bg-surface-sunken border-b border-hairline">
          <h3 className="text-sm font-semibold text-ink">
            Ultimate Beneficial Owners ({ubos.length})
          </h3>
        </div>
        <ul className="divide-y divide-hairline" role="list" aria-label="UBO list">
          {ubos.map((ubo) => (
            <li key={ubo.id} className="px-4 py-3 hover:bg-surface-sunken">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink truncate">
                    {ubo.name}
                  </p>
                  <div className="mt-1 flex items-center gap-3">
                    <PercentageBar percentage={ubo.ownershipPercentage} />
                    {ubo.nationality && (
                      <span className="text-xs text-ink-muted">
                        {ubo.nationality}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ms-4">
                  <StatusBadge value={ubo.verificationStatus} />
                  {ubo.linkedCaseId && (
                    <span className="text-xs text-primary font-mono">
                      {ubo.linkedCaseId.slice(0, 12)}...
                    </span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Ownership Chain Visualization */}
      {chains && chains.length > 0 && (
        <div className="bg-surface-raised rounded-sm shadow overflow-hidden">
          <div className="px-4 py-3 bg-surface-sunken border-b border-hairline">
            <h3 className="text-sm font-semibold text-ink">
              Ownership Chains
            </h3>
          </div>
          <div className="p-4 space-y-4">
            {chains.map((chain, chainIndex) => (
              <div
                key={`chain-${chainIndex}`}
                className="rounded-sm border border-hairline p-3"
              >
                <div className="text-xs text-ink-muted mb-2">
                  Chain {chainIndex + 1} — Effective ownership:{' '}
                  <span className="font-semibold text-ink">
                    {chain.effectiveOwnership.toFixed(1)}%
                  </span>
                </div>
                {chain.links.map((link, linkIndex) => (
                  <React.Fragment key={link.nodeId}>
                    {linkIndex > 0 && <ChainArrow />}
                    <div
                      className={`flex items-center gap-3 rounded px-3 py-2 ${
                        link.nodeType === 'person'
                          ? 'bg-primary-tint border border-primary/30'
                          : 'bg-surface-sunken border border-hairline'
                      }`}
                    >
                      <span
                        className={`flex h-6 w-6 items-center justify-center rounded-sm text-xs font-medium ${
                          link.nodeType === 'person'
                            ? 'bg-primary-tint text-primary'
                            : 'bg-surface-sunken text-ink'
                        }`}
                      >
                        {link.nodeType === 'person' ? 'P' : 'E'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-ink truncate">
                          {link.name}
                        </p>
                      </div>
                      {linkIndex > 0 && (
                        <PercentageBar percentage={link.ownershipPercentage} />
                      )}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
